import { useState } from 'react';
import { Form, Button, Table, Spinner, Image } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useUpdateRentMutation } from '../../slices/rentsApiSlice';

const RentEditImportImagesScreen = () => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [updateRent, { isLoading: loadingUpdate }] = useUpdateRentMutation();

  const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

  const validateRow = (row) => {
    if (!row._id || !isValidObjectId(row._id.trim())) return 'Invalid or missing ID';
    if (!row.house || !row.tenant || !row.landlord || !row.startDate || !row.endDate || !row.monthlyRent || !row.numberOfMonths || !row.address || !row.date) {
      return 'Missing required fields';
    }
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = parsed.map((row, index) => ({
        index,
        _id: row._id?.trim(),
        house: row.house,
        tenant: row.tenant,
        landlord: row.landlord,
        startDate: row.startDate,
        endDate: row.endDate,
        lastRentEnd: row.lastRentEnd,
        monthlyRent: row.monthlyRent,
        numberOfMonths: row.numberOfMonths,
        address: row.address,
        date: row.date,
        datePaid: row.datePaid,
        remark: row.remark,
        images: [],
        error: validateRow(row),
        status: null,
      }));

      setRows(enriched);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = async (files, rowIndex) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024;

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Unsupported file type: ${file.name}`);
        return;
      }
      if (file.size > maxSize) {
        toast.error(`File too large: ${file.name}`);
        return;
      }
    }

    const uploadData = new FormData();
    Array.from(files).forEach((file) => uploadData.append('images', file));

    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
      });
      const data = await res.json();
      if (res.ok) {
        const updated = [...rows];
        updated[rowIndex].images = [
          ...updated[rowIndex].images,
          ...data.images.map((img) => (typeof img === 'string' ? img : img.path || '')),
        ].filter(Boolean);
        setRows(updated);
        toast.success(`Images uploaded for row ${rowIndex + 1}`);
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRowSubmit = async (row, index) => {
    if (row.error) {
      toast.warning(`Row ${index + 1} skipped: ${row.error}`);
      return;
    }

    try {
      await updateRent({
        rentId: row._id,
        updatedData: {
          house: row.house,
          tenant: row.tenant,
          landlord: row.landlord,
          startDate: row.startDate,
          endDate: row.endDate,
          lastRentEnd: row.lastRentEnd,
          monthlyRent: row.monthlyRent,
          numberOfMonths: row.numberOfMonths,
          address: row.address,
          date: row.date,
          datePaid: row.datePaid,
          remark: row.remark,
          images: row.images,
        },
      }).unwrap();

      const updated = [...rows];
      updated[index].status = { type: 'Success', message: 'Updated' };
      setRows(updated);
      toast.success(`Row ${index + 1} updated`);
    } catch (err) {
      const updated = [...rows];
      updated[index].status = {
        type: 'Failed',
        message: err?.data?.message || err.message,
      };
      setRows(updated);
      toast.error(`Row ${index + 1} failed: ${updated[index].status.message}`);
    }
  };

  return (
    <div className="container mt-4">
      <h2>🖼️ Bulk Rent Image Import</h2>

      <Form.Group controlId="excelFile" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
      </Form.Group>

      {rows.length > 0 && (
        <>
          <h5>Preview & Edit: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Tenant</th>
                <th>House</th>
                <th>Start</th>
                <th>End</th>
                <th>Images</th>
                <th>Upload</th>
                <th>Submit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td>{row._id}</td>
                  <td>{row.tenant}</td>
                  <td>{row.house}</td>
                  <td>{row.startDate}</td>
                  <td>{row.endDate}</td>
                  <td>
                    {row.images?.map((img, i) => (
                      <Image
                        key={i}
                        src={img}
                        alt={`img-${i}`}
                        thumbnail
                        style={{ width: '40px', height: '40px', marginRight: '4px' }}
                      />
                    ))}
                  </td>
                  <td>
                    <Form.Control
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files, idx)}
                    />
                  </td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleRowSubmit(row, idx)}
                      disabled={loadingUpdate}
                    >
                      {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </Button>
                  </td>
                  <td>
                    {row.status ? (
                      <span
                        className={
                          row.status.type === 'Success'
                            ? 'text-success'
                            : row.status.type === 'Skipped'
                            ? 'text-warning'
                            : 'text-danger'
                        }
                      >
                        {row.status.type} — {row.status.message}
                      </span>
                    ) : row.error ? (
                      <span className="text-danger">{row.error}</span>
                    ) : (
                      <span className="text-muted">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
};

export default RentEditImportImagesScreen;